import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '../../components/Button/Button';
import { AngleLeft } from '../../icons/AngleLeft';
import { PatientRecord, PatientsAjaxResult, Section } from '../../shared/types/types';
import { PatientInfo } from '../patient/info/PatientInfo';

import './PatientCheckIn.scss';
import { useCallback, useContext, useState, useEffect } from 'react';
import { fetchAuthenticated } from '../../controllers';
import { WAS_BACK_END_API_URL, URL_VERSION, WEB_OWNER, URL_TAS_VERSION, TAS_BACK_END_API_URL } from '../../constants';
import { decryptObject, encryptObject } from '../../components/Encryption/obfuscationHandler';
import { BannerMessageContext } from 'src/shared/contexts/BannerMessageContext';
import { v4 } from 'uuid';
import { PopupMessageContext } from 'src/shared/contexts/PopupMessageContext';
import { CheckIn } from './checkIn/CheckIn';

export interface PatientProps {
  section: Section;
  token: string | null;
}

export interface PatientIdInfo {
  id?: string;
  token: string | null;
  version?: string;
}

export interface PatientDataProps {
  patientIdInfo: PatientIdInfo;
}

export interface PatchPatientProps {
  patientIdInfo: PatientIdInfo;
  payload?: Partial<PatientRecord>;
}

export const PatientCheckIn = ({ section, token }: PatientProps) => {
  const navigate = useNavigate();
  const { id } = useParams();
  const patientId = typeof id === 'string' ? id : '';

  const [patientInfo, setPatientInfo] = useState<PatientRecord>();
  const [patientInfoIsLoading, setPatientInfoIsLoading] = useState(false);
  const [patientInfoError, setPatientInfoError] = useState<any>();

  const [profilePhotoUrl, setProfilePhotoUrl] = useState<string>('');
  const [imageString, setImageString] = useState<string>('');
  const [tempPatientInfo, setTempPatientInfo] = useState<Partial<PatientRecord>>({});

  const { addBannerMessage } = useContext(BannerMessageContext);
  const { addPopupMessage } = useContext(PopupMessageContext);

  const decryptAndSavePatientInfo = useCallback(
    async (encryptedPatientInfo: any, profilePhotoS3Url: string) => {
      const decryptedData = { ...encryptedPatientInfo };
      await decryptObject(decryptedData, token, null);
      setProfilePhotoUrl(profilePhotoS3Url);
      setPatientInfo(decryptedData);
    },
    [token]
  );

  const getPatientInfo = useCallback(
    async ({ patientIdInfo: { id: patientId, token } }: PatientDataProps) => {
      const url = `${TAS_BACK_END_API_URL}${URL_TAS_VERSION}/treatablepatients/${patientId}`;
      try {
        setPatientInfoIsLoading(true);
        const config: RequestInit = {
          method: 'GET',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const response = await fetchAuthenticated<any>(url, config);

        if (response?.ok) {
          if (response.parsedBody?.result) {
            let patientData = response.parsedBody.result.cas_data.length === 1 ? response.parsedBody.result?.cas_data[0] : response.parsedBody.result?.tds_data[0];
            patientData.patientDoc['obfuscationId'] = response.parsedBody.result.obfuscationId;
            await decryptAndSavePatientInfo(patientData?.patientDoc, patientData?.profilePhotoUrl);
          }
        }

        setPatientInfoIsLoading(false);
      } catch (e) {
        setPatientInfoIsLoading(false);
        setPatientInfoError(e);
        addBannerMessage({
          id: v4(),
          message: 'Uh oh! There was an error getting the patient data. Please refresh the page to try again.',
          type: 'error',
        });
      }
    },
    [decryptAndSavePatientInfo, addBannerMessage]
  );

  const patchPatientInfo = async ({ patientIdInfo: { id: patientId, token, version }, payload }: PatchPatientProps) => {
    if (!payload) return;
    const url = `${WAS_BACK_END_API_URL}${URL_VERSION}/patients/${patientId}?archived=true`;
    try {
      setPatientInfoIsLoading(true);
      const config: RequestInit = {
        method: 'PATCH',
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(
          await encryptObject(
            {
              _version: version,
              ...tempPatientInfo,
              owner: WEB_OWNER,
            },
            token,
            null
          )
        ),
      };
      const response = await fetchAuthenticated<PatientsAjaxResult>(url, config);

      if (response?.ok) {
        if (response.parsedBody?.result?.patients) {
          await decryptAndSavePatientInfo(response.parsedBody.result, response?.parsedBody?.result?.profilePhotoUrl);
        }
      }
      setPatientInfoIsLoading(false);
    } catch (e) {
      setPatientInfoIsLoading(false);
      addPopupMessage({
        id: v4(),
        message: 'There was an error changing the patient data. Please retry request.',
        header: 'Change',
      });
    }
  };

  const getProfilePhotoUrl = useCallback(async (photoUrl: string) => {
    if (!photoUrl || photoUrl === '') return;
    try {
      await fetch(photoUrl)
        .then((response) => response.text())
        .then((result) => {
          setImageString(result);
        });
    } catch (error) {
      setImageString('');
    }
  }, []);

  useEffect(() => {
    if (!profilePhotoUrl || profilePhotoUrl === '') return;
    getProfilePhotoUrl(profilePhotoUrl);
  }, [profilePhotoUrl, getProfilePhotoUrl]);

  // get patient info when the component mounts

  useEffect(() => {
    getPatientInfo({ patientIdInfo: { id, token } });
  }, [getPatientInfo, id, token]);

  const handleUpdatePatientInfo = async () => {
    if (!patientInfo) return;
    await patchPatientInfo({
      patientIdInfo: {
        id: patientId,
        token,
        version: patientInfo._version,
      },
      payload: tempPatientInfo,
    });
  };

  return (
    <div className="patient-container">
      <PatientInfo
        patientData={patientInfo}
        isLoading={patientInfoIsLoading}
        error={patientInfoError}
        profilePhotoString={imageString}
        onUpdatePatientInfo={handleUpdatePatientInfo}
        setTempPatientInfo={setTempPatientInfo}
        tempPatientInfo={tempPatientInfo}
      />
      <CheckIn patientData={patientInfo} token={token} patientInfoIsLoading={patientInfoIsLoading} />

      <div className="patient-controls">
        <Button
          label={'Back'}
          className={'go-back-button'}
          onClick={() => {
            navigate('/');
          }}
          icon={<AngleLeft />}
          iconPlacement={'left'}
        />
      </div>
    </div>
  );
};
